import { useEffect, useState } from "react";
import {
  Card,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Alert,
  Stack,
  Typography,
} from "@mui/material";
import useNavStore from "../../../stores/nav";
import useAuthStore from "../../../stores/auth";
import { fetchData } from "../../../fetchData";
import { Progress } from "../../../components/DataGrid";
import HistoryDetailDrawer from "./drawers/historyDetailDrawer";

type Order = "asc" | "desc";
type OrderBy = "type" | "username" | "registered_at";

interface HistoryRow {
  type: string;
  username: string;
  registered_at: string;
  data: {
    reference: number;
    name?: string;
    date?: string;
    period_init_date?: string;
    vacation_init_date?: string;
    days?: number;
    advance_13o?: boolean;
    allowance_days?: number;
    observations?: string;
    type?: string;
    reason?: string;
    obs?: string;
    start_date?: string;
    end_date?: string;
    is_work_accident?: string | boolean;
    had_previous_absence?: string | boolean;
  };
  id: string;
}

function RequestStatus() {
  const setHeaderTitle = useNavStore((state) => state.setHeader);
  const company = useAuthStore((state) => state.company);
  const [initProgress, setInitProgress] = useState(true);
  const [progress, setProgress] = useState(false);
  const [page, setPage] = useState(1);
  const [showLoadMore, setShowLoadMore] = useState(true);
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<OrderBy>("registered_at");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<HistoryRow | null>(null);

  const loadData = async () => {
    if (!company?.reference) return;

    setProgress(true);
    try {
      const skip = (page - 1) * 10;
      const response = await fetchData(
        `/organizations/${company.reference}/requests-history?limit=10&skip=${skip}`,
        { method: "GET" },
      );

      if (response && Array.isArray(response)) {
        if (response.length === 0) {
          setShowLoadMore(false);
        } else {
          setRows([...rows, ...response]);
        }
      }
    } catch (error) {
      setShowLoadMore(false);
    }
    setProgress(false);
    setInitProgress(false);
  };

  useEffect(() => {
    loadData();
    setHeaderTitle("Solicitações");
  }, []);

  useEffect(() => {
    if (page > 1) {
      loadData();
    }
  }, [page]);

  const formatDate = (date: string) => {
    if (!date) return "";
    return new Date(date).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const translateType = (type: string) => {
    switch (type) {
      case "fopag_termination":
        return "Rescisão";
      case "fopag_vacation":
        return "Férias";
      case "fopag_absence":
        return "Atestado/Afastamento";
      case "ticket":
        return "Chamado";
      default:
        return type || "Não identificado";
    }
  };

  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedRows = [...rows].sort((a, b) => {
    if (orderBy === "registered_at") {
      return order === "desc"
        ? new Date(b.registered_at).getTime() -
            new Date(a.registered_at).getTime()
        : new Date(a.registered_at).getTime() -
            new Date(b.registered_at).getTime();
    }

    const aValue = orderBy === "type" ? translateType(a[orderBy]) : a[orderBy];
    const bValue = orderBy === "type" ? translateType(b[orderBy]) : b[orderBy];
    return order === "desc"
      ? bValue.localeCompare(aValue)
      : aValue.localeCompare(bValue);
  });

  const getEmployeeName = (row: HistoryRow) => {
    if (
      row.type === "fopag_vacation" ||
      row.type === "fopag_termination" ||
      row.type === "fopag_absence"
    ) {
      return row.data.name || "N/A";
    }
    return "-";
  };

  return (
    <>
      <Card>
        {!initProgress && rows.length === 0 ? (
          <Alert
            severity="info"
            sx={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              p: 2,
            }}
          >
            <Stack spacing={2} alignItems="center" direction="column">
              <Typography>Nenhuma solicitação encontrada.</Typography>
            </Stack>
          </Alert>
        ) : (
          <>
            <Table
              sx={{ minWidth: 650 }}
              aria-label="histórico de solicitações"
            >
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "type"}
                      direction={orderBy === "type" ? order : "asc"}
                      onClick={() => handleRequestSort("type")}
                    >
                      Tipo
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "username"}
                      direction={orderBy === "username" ? order : "asc"}
                      onClick={() => handleRequestSort("username")}
                    >
                      Requerente
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Colaborador</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "registered_at"}
                      direction={orderBy === "registered_at" ? order : "asc"}
                      onClick={() => handleRequestSort("registered_at")}
                    >
                      Data do Registro
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    onClick={() => {
                      setSelectedRow(row);
                      setDrawerOpen(true);
                    }}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>{translateType(row.type)}</TableCell>
                    <TableCell>{row.username || "N/A"}</TableCell>
                    <TableCell>{getEmployeeName(row)}</TableCell>
                    <TableCell>{formatDate(row.registered_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {progress && !initProgress && <Progress />}
            {showLoadMore && !progress && rows.length > 0 && (
              <Button
                fullWidth
                sx={{ mt: 2 }}
                onClick={() => setPage(page + 1)}
              >
                Carregar mais
              </Button>
            )}
          </>
        )}
      </Card>
      <HistoryDetailDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedRow(null);
        }}
        data={selectedRow}
      />
    </>
  );
}

export default RequestStatus;
